import type { ComponentIndexes } from "common/component-indexes";
import type { AppKind, SerializedApp, SourceMetadata } from "common/components/SerializedApp";
import { getAppKind } from "common/components/SerializedApp";
import type {
    AppAnalytics,
    AppGrandfathering,
    AppMetadata,
    CompatibilityProblems,
    Organization,
    Owner,
    TemplateSubmissions,
    UserDataAndState,
    WebhookIntegrationWithID,
    ZapData,
} from "common/Database";
import { getAppEminence, isUserData } from "common/Database";
import type { TableName } from "common/database-strings";
import type { BuilderAction } from "common/Database/builder-actions";
import type { EminenceFlags } from "common/Database/eminence";
import { Eminence, eminenceFlags } from "common/Database/eminence";
import type { Pricing } from "common/Database/pricing";
import type { AllQuotaValuesWithLimitsForKinds } from "common/Database/quotas";
import { QuotaKind } from "common/Database/quotas";
import type { TileDescription as TileDescriptionType } from "common/Database/tile-type";
import type { TableColumn, TypeSchema } from "common/description";
import { areTableNamesEqual } from "common/description";
import type { DeviceFormFactor } from "common/render/form-factor";
import { ArrayMap } from "support/array-map";
import type { PasteboardState } from "generator/components";
import type { SearchedColumn } from "generator/components/description-handlers";
import type { HeaderSection } from "generator/components/property-sections";
import type { SelectMode } from "sharedUI/lib/editor-select-mode-utils";
import type { MaterialGlideAppState } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import type { ForwardingAppEnvironment } from "../lib/forwarding-app-environment";
import type { TablesState } from "./tables-state";

export type TileDescription = TileDescriptionType;

export interface PublishState {
    readonly isPublished: boolean;
    readonly isPublishingActive: boolean;
    readonly publishedAt: Date | undefined;
    // "again" means currently publishing, and after it's done, should be published again
    readonly publishing: boolean | "again";
    readonly appURL: string | undefined;
    readonly shortName: string | undefined;
    readonly customDomain: string | undefined;
    readonly desiredDomain: string | undefined;
    readonly error: string | undefined;
}

export type PublishStateOrLoading = PublishState | "loading";
export type AppMetadataOrLoading = AppMetadata | "loading";

export interface AppAndState {
    readonly sourcePad: SourcePadState;
    readonly serializedApp: SerializedApp;
    readonly serial: number;
    readonly builderActions: Map<string, BuilderAction>;
    readonly appState: MaterialGlideAppState | undefined;
    readonly reloadIssues: CompatibilityProblems | undefined;
    readonly publishState: PublishStateOrLoading;
    readonly appMetadata: AppMetadataOrLoading;
    readonly analytics: AppAnalytics | undefined;
    readonly zapierAPIKey: string | undefined;
    readonly zaps: readonly ZapData[];
    readonly webhooks: readonly WebhookIntegrationWithID[];
    readonly quotas: AllQuotaValuesWithLimitsForKinds | undefined;
    // The user ID of the owner.  We use this to not try to save
    // another user's app that's opened by an admin, because that
    // would give an error from Firestore and reload the builder.
    readonly userID: string | undefined;
    readonly grandfathering: AppGrandfathering | undefined;
}

export interface AppsAndState {
    [id: string]: AppAndState | "loading";
}

export interface TileDescriptionsState {
    [id: string]: TileDescription | "loading";
}

export interface TemplateStoreInfoForTemplate {
    readonly title: string;
    readonly author: string | undefined;
    readonly email: string | undefined;
    readonly pricing: Pricing;
}

export interface TemplateStoreState {
    [templateID: string]: TemplateStoreInfoForTemplate;
}

export interface Video {
    title: string;
    category: string;
    url: string;
}

export interface VideosState {
    readonly videos: readonly Video[];
}

interface CommentBlock {
    kind: "comment";
    data: string;
    id: string;
}

interface HeaderBlock {
    kind: "header";
    data: string;
    id: string;
}

interface ColumnBlock {
    kind: "column";
    column: string;
    hidden?: boolean;
}

export type Block = CommentBlock | HeaderBlock | ColumnBlock;

export interface NotebookWithTableName {
    readonly tableName: TableName;
    // `undefined` means "loading"
    readonly blocks: readonly Block[] | undefined;
}

export interface NotebookState {
    readonly notebooks: readonly NotebookWithTableName[];
}

export interface SearchState extends SearchedColumn {
    readonly showSearch: boolean;
    readonly childColumnNames?: readonly string[];
}

export function findNotebookForTable(
    notebooks: readonly NotebookWithTableName[],
    tableName: TableName
): NotebookWithTableName | undefined {
    return notebooks.find(n => n.tableName.name === tableName.name);
}

export function makeColumnsHiddenForNotebook(notebook: readonly Block[]): ReadonlySet<string> {
    const columnsHidden = new Set<string>();
    for (const b of notebook) {
        if (b.kind !== "column") continue;
        if (b.hidden === true) {
            columnsHidden.add(b.column);
        }
    }
    return columnsHidden;
}

export function makeColumnsHiddenForNotebooks(
    notebooks: readonly NotebookWithTableName[]
): ArrayMap<TableName, ReadonlySet<string>> {
    return new ArrayMap(
        areTableNamesEqual,
        notebooks.map(n => [n.tableName, makeColumnsHiddenForNotebook(n.blocks ?? [])])
    );
}

export function removeColumnsHiddenForNotebook(
    columns: readonly TableColumn[],
    columnsHidden: ReadonlySet<string> | undefined,
    selectedColumnPath: readonly string[] | undefined,
    dontHide: boolean
): readonly TableColumn[] {
    if (columnsHidden === undefined || dontHide) {
        return columns;
    }
    const selectedColumnName = selectedColumnPath?.length === 1 ? selectedColumnPath[0] : undefined;
    return columns.filter(c => !columnsHidden.has(c.name) || c.name === selectedColumnName);
}

export type OrganizationsState = readonly Organization[];

export interface RootState {
    apps: AppsAndState;
    templateStore: TemplateStoreState;
    tiles: TileDescriptionsState;
    userData: UserDataAndState;
    organizations: OrganizationsState;
    // FIXME: organizations: OrganizationsState | false;
    // Unfortunately too many code paths depend on it being
    // just an array, so to work around that, we use this flag.
    organizationsLoaded: boolean;
    uiState: UIState;
    tables: TablesState;
    newComponents: ReadonlySet<string>;
    templateSubmissions: TemplateSubmissions;
    notebook: NotebookState;
    search: SearchState;
    pasteboard: PasteboardState;
}

export interface LeftPanelState {
    shouldExpand: boolean;
    copyingSample: boolean;
}

export interface SelectedComponentPath {
    appID: string;
    screenName: string;
    indexes: ComponentIndexes;
    subcomponent?: string;
}

export interface UIState {
    readonly leftPanelState: LeftPanelState;
    readonly previewPlatform: "iOS" | "Android";
    readonly deviceFormFactor: DeviceFormFactor;
    readonly maxPageWidth: number | undefined;
    readonly selectedComponent: SelectedComponentPath | undefined;
    readonly selectedColumn: SelectedComponentPath | undefined;
    readonly selectMode: SelectMode;
    readonly desiredHeaderSection: HeaderSection | undefined;
    readonly highlightComponent:
        | {
              readonly id: string;
              readonly applyTo: "App" | "Designer";
          }
        | undefined;
}

export interface SourcePadState {
    readonly sourceMetadata: readonly SourceMetadata[];
    readonly spreadsheetName: string | undefined;
    readonly schema: TypeSchema;
}

export interface QuotaState {
    readonly rowQuota: number;
    readonly geocodeQuota: number;
    readonly zapsQuota: number;
    readonly reloadQuota: number;
    readonly deliverEmailQuota: number;
    readonly fileSizeQuota: number;
}

function currentQuotaStateFor(state: AppAndState | undefined, quotaKind: QuotaKind): number {
    return state?.quotas?.[quotaKind]?.current ?? 0;
}

export function getQuotaStateFromApp(
    state: AppAndState | undefined,
    appEnvironment: ForwardingAppEnvironment | undefined
): QuotaState {
    const geocodeQuota = appEnvironment?.getHighestGeocodeCount() ?? 0;
    const rowQuota = currentQuotaStateFor(state, QuotaKind.RowsUsed);
    const zapsQuota = currentQuotaStateFor(state, QuotaKind.ZapsAndWebhook);
    const reloadQuota = currentQuotaStateFor(state, QuotaKind.Reloads);
    const deliverEmailQuota = currentQuotaStateFor(state, QuotaKind.DeliverEmail);
    const fileSizeQuota = currentQuotaStateFor(state, QuotaKind.FileBytesUsed);
    return { geocodeQuota, rowQuota, zapsQuota, reloadQuota, deliverEmailQuota, fileSizeQuota };
}

export function getQuotaStateForAnalytics(state: QuotaState) {
    const {
        geocodeQuota: geocode_quota,
        rowQuota: row_quota,
        zapsQuota: zaps_quota,
        reloadQuota: reload_quota,
        deliverEmailQuota: deliver_email_quota,
        fileSizeQuota: file_size_quota,
    } = state;
    return {
        geocode_quota,
        row_quota,
        zaps_quota,
        reload_quota,
        deliver_email_quota,
        file_size_quota,
    };
}

export function getCurrentAppAndState(appID: string | undefined | null, apps: AppsAndState): AppAndState | undefined {
    if (appID === undefined || appID === null) return undefined;

    const maybeAppAndState: AppAndState | "loading" | undefined = apps[appID];
    if (maybeAppAndState === undefined || maybeAppAndState === "loading") {
        return undefined;
    }
    return maybeAppAndState;
}

export function getCurrentAppAndStateOrDefaults(appID: string | undefined, apps: AppsAndState): Partial<AppAndState> {
    if (appID === undefined) return {};
    const currentAppAndState = getCurrentAppAndState(appID, apps);
    if (currentAppAndState === undefined) return {};
    return currentAppAndState;
}

export function getAppOwner(
    appID: string,
    userData: UserDataAndState,
    organizations: OrganizationsState
): Owner | undefined {
    if (isUserData(userData) && userData.appIDs.indexOf(appID) >= 0) {
        return userData;
    }
    for (const org of organizations) {
        if (org.appIDs.indexOf(appID) >= 0) {
            return org;
        }
    }
    return undefined;
}

export function getAppKindFromState(appID: string, state: RootState): AppKind | undefined {
    const appAndState = getCurrentAppAndState(appID, state.apps);
    if (appAndState === undefined) return undefined;
    return getAppKind(appAndState.serializedApp);
}

export function getAppEminenceFlagsFromState(
    appID: string | undefined,
    {
        userData,
        organizations,
        apps,
    }: {
        userData: UserDataAndState;
        organizations: OrganizationsState;
        apps: AppsAndState;
    },
    // This used when we're making a new app/page for which there is no entry
    // in the state yet.
    appKindOverride?: AppKind
): EminenceFlags {
    if (appID === undefined) return eminenceFlags[Eminence.Free];
    const owner = getAppOwner(appID, userData, organizations);
    // This is a little tricky: if we get here before the userData does, we expect branding
    // to temporarily be off. If the app is Pro, it stays off. If the app isn't Pro, it
    // pops back in.
    if (owner === undefined) return eminenceFlags[Eminence.Pro];
    const appAndState = getCurrentAppAndState(appID, apps);
    let actualAppKind: AppKind;
    if (appKindOverride === undefined) {
        // Same thing here.
        if (appAndState === undefined) return eminenceFlags[Eminence.Pro];
        actualAppKind = getAppKind(appAndState.serializedApp);
    } else {
        actualAppKind = appKindOverride;
    }
    const publishState = appAndState?.publishState;
    const isPublished = publishState === undefined || publishState === "loading" ? false : publishState.isPublished;
    return eminenceFlags[getAppEminence(owner, appID, actualAppKind, isPublished)];
}
